import format from 'string-template';
import { toJS } from 'mobx';
import { makeObservable, observable, action, computed, reaction } from 'mobx';
import { getFromLocalStorage, saveToLocalStorage } from '/@/utils/localStorage';
import { numberOrNull } from '/@/app/utils';
import { LOCAL_STORAGE_KEY, LOCAL_STORAGE_KEY_VERIFY_ERROR } from './localStorage';

//
// Measurement observable
//

export class MeasurementState {
    api=null;
    // Constant fields
    panelId;
    xlsxId;
    caption;
    localStorageKey;
    localStorageKeyVerifyError;
    value_is_computed;
    unitsAvailable;

    // Auxiliary fields
    saveState;
    // Keeps user input during typing:
    // e.g. `10.` is allowed, `10.0` is not replaced with `10`.
    typedText = null;

    // Dynamic fields
    value;   // currently visible & stored locally
    savedValue;  // saved to server

    units;  // current visible units

    constructor(fullMeasurement, panelId, api) {
	this.api=api;
        // Use these as constants.
        const labTest = fullMeasurement.lab_test;
        const measurement = fullMeasurement.measurement;  // can be null
        this.unitsAvailable = fullMeasurement.units?.alternative || [];

        this.panelId = panelId;
        this.xlsxId = labTest.xlsx_id;
        this.caption = labTest.caption;
        this.value_is_computed = labTest.value_is_computed;
        this.savedValue = {
            value: numberOrNull(measurement?.value),
            // always reset to default units
            valueAlternativeUnits: numberOrNull(measurement?.value),
            alternativeUnits: null,
        };
        this.defaultUnits = labTest.units;

        // static items
        this.article_link = labTest.article_link;
        this.sound_link = labTest.sound_link;
        this.measurement = fullMeasurement.measurement;

        // Set value or load from local storage
        let _value = this.savedValue;
        this.localStorageKey = format(LOCAL_STORAGE_KEY, {
            panelId: this.panelId,
            xlsxId: this.xlsxId
        });
        this.localStorageKeyVerifyError = format(LOCAL_STORAGE_KEY_VERIFY_ERROR, {
            panelId: this.panelId,
            xlsxId: this.xlsxId
        });

        const _localValue = getFromLocalStorage(this.localStorageKey, null);
        if (_localValue !== null) {
            _value = {
                value: _localValue,
                valueAlternativeUnits: _localValue,
                alternativeUnits: null,
            }
        }
        this.value = _value;
        // Don't verify on page load, it can cause infinite error loops [FIXME investigate it]
        this.updateSaveState(_value, true);
        if (this.saveState !== "saved" && getFromLocalStorage(this.localStorageKeyVerifyError, false)) {
            this.saveState = 'error';
        }
        //
        makeObservable(this, {
            value: observable,  // includes alternative unit
            savedValue: observable,
            saveState: observable,
            typedText: observable,
            //
            nonOptimal: computed,
            //
            setInputValue: action,
            setInputUnits: action,
            //
            replaceValue: action,
            cleanValueType: action,
            setTypedText: action,
            setSavedValue: action,
            setSaveState: action,
            resetToDefaultUnits: action,
            //
            updateSaveState: action,
        });

        // Save the value locally on change.
        reaction(() => this.value, (value) => {
            saveToLocalStorage(this.localStorageKey, value?.value);
        });
    }

    updateSaveState(valueWithUnits, dontVerify) {
        if (dontVerify === undefined) {
            dontVerify = false;
        }

        const needsSave = this.valueNeedsSave(valueWithUnits.valueAlternativeUnits);
        if (
            !needsSave
            // FIXME when/if value types will be introduced, organize conversion of strings to floats here
            || !this.savedValue && !valueWithUnits
        ) {
            this.setSaveState("saved");
        } else {
            this.setSaveState("unsaved");
            if (!dontVerify) {
                this.scheduleVerify();
            }
        }
    }

    setTypedText(text) {
        this.typedText = text;
    }

    setInputValue(newValue) {
        newValue = numberOrNull(parseFloat(newValue));
        // When setting input only, update the underlying `this.value.value`.
        // It's used for diagrams, warnings against range, etc.
        const units = this.unitsAvailable.find((unit) => unit.id === this.value?.alternativeUnits);
        if (!units) {
            // editing base value
            this.value = {
                value: newValue,
                valueAlternativeUnits: newValue,
                alternativeUnits: null,
            };
        } else {
            this.value = {
                value: newValue * units.to_default,
                valueAlternativeUnits: newValue,
                alternativeUnits: units.id,
            };
        }

        this.updateSaveState(this.value);
    }

    setInputUnits(unitsId) {
        // When the user changes units, typed text is no longer relevant.
        // Clear it, so it won't hide the real value.
        this.setTypedText(null);

        // When replacing the entire value, update the current alternative units,
        // so user can see values changing.
        const units = this.unitsAvailable.find((unit) => unit.id === unitsId);
        let valueAlternativeUnits = null;
        if (numberOrNull(this.value?.value) !== null) {
            if (!units) {
                valueAlternativeUnits = this.value.value;
            } else {
                valueAlternativeUnits = this.value.value / units.to_default;
            }
        }

        this.value = {
            ...toJS(this.value),
            alternativeUnits: unitsId,
            valueAlternativeUnits: valueAlternativeUnits,
        };
        this.updateSaveState(this.value);
    }
    replaceValue(valueWithUnits) {
        this.setTypedText(null);
        this.value = toJS(valueWithUnits);
        this.updateSaveState(this.value);
    }
    resetToDefaultUnits() {
        this.value = {
            value: numberOrNull(this.value?.value),
            valueAlternativeUnits: numberOrNull(this.value?.value),
            alternativeUnits: null,
        };
    }

    setSavedValue(value) {
        this.savedValue = value;
    }

    setSaveState(state) {
        if (!["saved", "unsaved", "verified", "error"].includes(state)) {
            throw new Error(`Invalid save state "${state}"`);
        }
        this.saveState = state;
        //
        if (["saved", "verified"].includes(state)) {
            saveToLocalStorage(this.localStorageKeyVerifyError, false);
        }
        if (state === "error") {
            saveToLocalStorage(this.localStorageKeyVerifyError, true);
        }
    }

    VERIFY_DELAY = 500;  // ms
    verifyTimeoutId = null;

    scheduleVerify(delay) {
        if (delay === undefined) {
            delay = this.VERIFY_DELAY;
        }

        if (this.verifyTimeoutId) {
            clearTimeout(this.verifyTimeoutId);
        }
        this.verifyTimeoutId = setTimeout(() => {
            this.verify();
        }, delay);
    }

    cancelVerify() {
        if (this.verifyTimeoutId) {
            clearTimeout(this.verifyTimeoutId);
            this.verifyTimeoutId = null;
        }
    }

    cleanValueType() {
        let val = numberOrNull(this.value?.valueAlternativeUnits);
        if (val === null) {
            val = "";
        } else if (typeof val === 'string') {
            val = val.trim();
            val = val.replace(",", ".");  // accept Russian dot style
        }
        //
        let floatVal;
        if (val === "") {   // FIXME? verify the logic.
            floatVal = null;
        } else {
            floatVal = parseFloat(val);
        }
        //
        // Client-side check
        //
        if (isNaN(floatVal)) {
            this.setSaveState("error");
            return null;
        }

        const needsSave = this.valueNeedsSave(floatVal);
        if (!needsSave
            || !this.savedValue && !floatVal) {
            this.setSaveState("saved");
            return floatVal;
        }
        if (floatVal === null) {
            this.setSaveState("verified");
            return floatVal;
        }
        if (needsSave) {
            this.setSaveState("verified");  // still need to save.
            return floatVal;
        }
        return floatVal;
    }

    verify() {
        const floatVal = this.cleanValueType()
        //
        // Server-side check
        //
        this.api.request(
            `cabinet/algorithm/measurements/${this.xlsxId}/check/`, {
                    xlsx_id: this.xlsxId,
                    value_alternative_units: floatVal,
                    alternative_units: this.value?.alternativeUnits,
                },
		{
                        method: 'PUT',
                        silentStatuses: {
                422: (error) => {
                    this.setSaveState("error")
                },
            }}).then((response) => {

            let needsSave = this.valueNeedsSave(floatVal);
            if (!needsSave) {
                this.setSaveState("saved");
            } else {
                this.setSaveState("verified");
            }
        }).catch((error) => {
            // skip. Don't retry.
            this.setSaveState("error");
        });
    }

    get nonOptimal() {
        if (numberOrNull(this.value?.value) === null) {
            return false;
        }
        const m = this.measurement;
        if (m && (m?.warning_bottom_edge !== null || m.warning_top_edge !== null)) {
            const bottom = (m.warning_bottom_edge !== null ? m.warning_bottom_edge : 0);
            const top = (m.warning_top_edge !== null ? m.warning_top_edge : Infinity);
            if (this.value.value < bottom || this.value.value > top) {
                return true;
            }
        }
        return false;
    }

    valueNeedsSave(altUnitsValue) {
        const newValue = {
            ...toJS(this.value),
            valueAlternativeUnits: altUnitsValue,
        };
        // double equality is correct here
        const valueUnChanged = this.savedValue?.value == newValue.value;
        return !valueUnChanged;
    }
}
